import { Inject, Injectable } from '@angular/core';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { Observable, of } from 'rxjs';
import {
    JsonNotificationStatusListModel,
    NotificationStatusListModel,
} from '../../interfaces/notification-status.interface';
import { NotificationStatusModelFromJson } from '../../models/low-level/notification-status.model';
import { KEY_NOTIFICATION_COMPLETED, KEY_NOTIFICATION_STATUS } from './storage-notification-status.service';
import { catchError, map } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';

@Injectable({ providedIn: 'root' })
export class StorageNotificationStatusConcurrentService {
    public constructor(
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public getAllNotificationsStatusModelFromStorage(): Observable<NotificationStatusListModel> {
        return this.concurrentUsageService.readDataForKey(KEY_NOTIFICATION_STATUS).pipe(
            map((result) => {
                if (result?.length > 0) {
                    const notificationStatusList: JsonNotificationStatusListModel = JSON.parse(result[0].value);
                    const jsonNotificationStatusModelList = notificationStatusList.notificationStatusList;
                    return {
                        notificationStatusList: jsonNotificationStatusModelList.map(
                            (jsonNotificationStatusModel) =>
                                new NotificationStatusModelFromJson(jsonNotificationStatusModel),
                        ),
                        lastSavedTimestamp: notificationStatusList.lastSavedTimestamp,
                    };
                }
                throw new Error(
                    '[Concurrency Store Notification Status] Read notification status: no notification status found in store.',
                );
            }),
            catchError((error) => {
                this.log.warn(`[Concurrency Store Notification Status] Read notification status: ${error}`);
                return of({ notificationStatusList: [], lastSavedTimestamp: 0 });
            }),
        );
    }

    public saveAllNotificationStatusModelToStorage(
        notificationStatusArray: NotificationStatusListModel,
    ): Observable<void> {
        const outData = JSON.stringify(notificationStatusArray);
        return this.concurrentUsageService
            .writeDataForKey(KEY_NOTIFICATION_STATUS, outData, notificationStatusArray.lastSavedTimestamp)
            .pipe(
                map(() => undefined),
                catchError((error) => {
                    this.log.warn(`[Concurrency Store Notification Status] Write notification status: ${error}`);
                    return of(undefined);
                }),
            );
    }

    public loadCompletedNotificationsToStorage(): Observable<NotificationCompletedListModel> {
        return this.concurrentUsageService.readDataForKey(KEY_NOTIFICATION_COMPLETED).pipe(
            map((result) => {
                if (result?.length > 0) {
                    return JSON.parse(result[0].value);
                }
                throw new Error(
                    '[Concurrency Store Notification Completed] Read completed notifications: no completed notifications found in store.',
                );
            }),
            catchError((error) => {
                this.log.warn(`[Concurrency Store Notification Completed] Read completed notification: ${error}`);
                return of(new NotificationCompletedListModel());
            }),
        );
    }

    public saveCompletedNotificationsToStorage(
        completedNotifications: NotificationCompletedListModel,
    ): Observable<void> {
        const outData = JSON.stringify(completedNotifications);
        return this.concurrentUsageService
            .writeDataForKey(KEY_NOTIFICATION_COMPLETED, outData, completedNotifications.lastSavedTimestamp)
            .pipe(
                map(() => undefined),
                catchError((error) => {
                    this.log.warn(`[Concurrency Store Notification Completed] Write completed notifications: ${error}`);
                    return of(undefined);
                }),
            );
    }
}
